import { Injectable } from '@angular/core';
import { assert } from '@sama/js-core';

// This service exists as a place to keep interactions with the browser so they
// can be stubbed in tests.
@Injectable({ providedIn: 'root' })
export class BrowserControlService {
  navigateTo(location: string) {
    window.location.href = location;
  }

  openInNewWindow(location: string) {
    return window.open(location, '_blank');
  }

  fetch(url: string) {
    return fetch(url);
  }

  openCaches(name: string) {
    return window.caches.open(name);
  }

  deleteCache(name: string) {
    return window.caches.delete(name);
  }

  getStorageEstimate() {
    return navigator.storage.estimate();
  }

  getSelection() {
    const selection = window.getSelection();
    assert(selection, 'Selection should be available');
    return selection;
  }

  // clears previous prefetch tags before setting the new ones, so this may not
  // be suitable if two components are trying to use it on the same page at the
  // same time
  preload(urls?: string[]) {
    const head = $('head');
    head.find('link[rel="prefetch"]').remove();
    for (const url of urls || []) {
      $('<link rel="prefetch" crossorigin="anonymous">')
        .attr('href', url)
        .appendTo(head);
    }
  }

  scrollToTop() {
    return window.scrollTo(0, 0);
  }

  readClipboardText() {
    return navigator.clipboard.readText();
  }

  writeClipboardText(text: string) {
    return navigator.clipboard.writeText(text);
  }

  requestAnimationFrame(handler: FrameRequestCallback) {
    requestAnimationFrame(handler);
  }

  documentHasFocus() {
    return document.hasFocus();
  }

  getPlatformModifier() {
    if (this.isMac()) {
      return 'metaKey';
    } else {
      return 'ctrlKey';
    }
  }

  isMac() {
    return navigator.platform.toLowerCase().includes('mac');
  }

  // Query is inspired from
  // https://css-tricks.com/snippets/css/retina-display-media-query/ and
  isRetina() {
    return window.matchMedia(
      '(-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi)',
    ).matches;
  }

  isCurrentHostPrototype() {
    return window.location.hostname.endsWith('.prototype.app.sama.com');
  }

  extractPrototypeName() {
    if (this.isCurrentHostPrototype()) {
      return window.location.hostname.replace('.prototype.app.sama.com', '');
    }

    return undefined;
  }

  alert(message: string) {
    alert(message);
  }

  getQueryParameters() {
    return window.location.search;
  }

  updateQueryParameters(...params: Array<[key: string, value: string]>) {
    const url = new URL(window.location.href);
    params.forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });
    history.pushState({}, '', url);
  }

  scrollIntoView(element: Element, options?: ScrollIntoViewOptions) {
    if (!isInViewport(element)) {
      element.scrollIntoView(options);
    }
  }
}

function isInViewport(element: Element) {
  const { top, left, bottom, right } = element.getBoundingClientRect();
  const { innerHeight, innerWidth } = window;
  return top >= 0 && left >= 0 && bottom <= innerHeight && right <= innerWidth;
}
